import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "routes";

import { useTheme, Stack, Typography } from "@mui/material";
import { ArrowUpRightIcon } from "lucide-react";

import { Button } from "@repo/ui";
import {
  ActivationPricingSchedule,
  EmptyState,
  Header,
  Loader,
  PlanIntervalToggle,
  planIntervalSavingsLabel,
} from "components";
import { capitalize, centsToDollar, date } from "utils";
import { activationTiersAgree } from "@repo/types";
import { useCompanyStore } from "stores/useCompanyStore";
import {
  useCompanySubscription,
  useSubscriptionPlans,
  SubscriptionPlan,
  useCancelSubscription,
  useGrantFreeMonth,
} from "hooks/api";
import { useSnackbar } from "contexts/SnackbarContext";
import { CancelSubscriptionModal } from "components/feedback/modals";
import { EnterpriseContactCard } from "./EnterpriseContactCard";
import { PlanComparisonMatrix, type PlanColumnAction } from "./PlanComparisonMatrix";

const matchesInterval = (plan: SubscriptionPlan, annual: boolean) => {
  const interval = (plan.interval || "").toString().toLowerCase();
  if (annual) {
    return (
      interval === "annual" || interval === "annually" || interval === "year"
    );
  }
  return interval === "month" || interval === "monthly";
};

export const BillingPlanChangePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [showAnnual, setShowAnnual] = useState(false);
  const { open: notify } = useSnackbar();
  const selectedCompany = useCompanyStore((state) => state.selectedCompany);
  const companyId = selectedCompany?.id ?? null;

  const {
    data: plans = [],
    isLoading: isLoadingPlans,
    isError: isPlansError,
  } = useSubscriptionPlans();

  const { data: subscription, isLoading: isLoadingSubscription } =
    useCompanySubscription(companyId, true);
  const cancelSubscription = useCancelSubscription();
  const grantFreeMonth = useGrantFreeMonth();

  const currentPlan = useMemo(() => {
    if (!subscription || subscription.status !== "active") return null;
    return (
      plans.find((plan) => plan.id === subscription.stripe_product_id) ?? null
    );
  }, [plans, subscription]);

  // Same lookup as `currentPlan` above but also covers "trialing" — a trialing
  // subscriber IS on a plan, and both the summary card and the matrix's
  // current-column highlight should say so.
  //
  // `currentPlan` stays "active"-only on purpose: it also decides upgrade vs
  // downgrade by price, and widening it here would change that comparison as a
  // side effect of a display fix. Splitting the two is what lets "which plan am I
  // on" and "which direction is this move" answer different questions.
  //
  // Worth stating because it was briefly wrong in exactly one direction: this
  // summary rendered for a trialing user while the matrix column stayed untinted,
  // so the page showed a plan at the top and highlighted none below it.
  const currentSubscribedPlan = useMemo(() => {
    if (
      !subscription ||
      (subscription.status !== "active" && subscription.status !== "trialing")
    ) {
      return null;
    }
    return (
      plans.find((plan) => plan.id === subscription.stripe_product_id) ?? null
    );
  }, [plans, subscription]);

  const filteredPlans = useMemo(() => {
    return plans
      .filter((plan) => plan.active !== false)
      .filter((plan) => matchesInterval(plan, showAnnual))
      .sort((a, b) => a.amount - b.amount);
  }, [plans, showAnnual]);

  /**
   * The "Save N%" pill's text, computed from the live catalog (see
   * `planIntervalSavings.ts` — it was the literal "Save 19%" until 2026-08-26,
   * a figure no tier has ever saved).
   *
   * Reads the BOTH-interval catalog rather than `filteredPlans`: the claim
   * compares a tier's monthly price against its annual one, so it needs both on
   * screen at once. Retired plans (`active === false`) are excluded for the same
   * reason they are excluded from the columns — a discount nobody can buy is not
   * a discount. `null` while plans load, which hides the pill.
   */
  const intervalSavingsLabel = useMemo(
    () => planIntervalSavingsLabel(plans.filter((plan) => plan.active !== false)),
    [plans],
  );

  // The bottom-of-page schedule table (VRG-375): a representative schedule to
  // render (the first filtered plan's own tiers — `ActivationPricingSchedule`
  // itself renders nothing if it's null/empty, AC4) plus whether every plan
  // actually agrees with it, which decides the heading's claim (AC5).
  const scheduleTiers = filteredPlans[0]?.activation_tiers ?? null;
  const scheduleAgreesAcrossPlans = activationTiersAgree(
    filteredPlans.map((plan) => plan.activation_tiers),
  );

  const isLoading =
    isLoadingPlans || (companyId ? isLoadingSubscription : false);

  const [openCancelSubscriptionModal, setOpenCancelSubscriptionModal] =
    useState(false);

  const handlePlanChange = (plan: SubscriptionPlan) => {
    const params = new URLSearchParams({
      planId: plan.id,
      priceId: plan.stripe_price_id,
    });
    navigate(
      `${
        ROUTES.SETTINGS_COMPANY_BILLING_PLAN_CHANGE_CHECKOUT
      }?${params.toString()}`
    );
  };

  /**
   * "Contact sales" on the Enterprise card.
   *
   * An in-app SUPPORT REQUEST, not the public vera.inc/contact form. It briefly went
   * to the public form on the reasoning that a lead form is what sales enquiries are
   * for — which is right for a stranger and wrong here. Whoever clicks this is an
   * authenticated customer we already know: their company, their plan, their usage.
   * Sending them to a public lead form makes them retype what we hold and treats a
   * paying brand as an anonymous visitor.
   *
   * KNOWN COST, not hidden: there is no \ request type, so this lands in the
   * support queue for a human to route. Adding one means an API enum, a badge, a
   * filter facet and a seed, and the intake form's own comment asks callers to resist
   * adding categories — so it is worth doing once this button proves it gets clicked,
   * not before. Tracked on VRG-393.
   */
  const handleContactSales = () => {
    navigate(`${ROUTES.SUPPORT_NEW}?type=support`);
  };

  // Per-plan computation that used to live inline inside the removed plan
  // cards' `.map()` — the values are unchanged, they just no longer build JSX
  // directly and instead feed the matrix header's `planAction` /
  // `planSavingsLabel` callbacks (and the current-plan summary card above it).
  const getPlanColumnInfo = (plan: SubscriptionPlan) => {
    const isCurrentPlan =
      !!subscription &&
      (subscription.status === "active" ||
        subscription.status === "trialing") &&
      subscription.stripe_product_id === plan.id;
    const priceLabel = centsToDollar(plan.amount);
    const intervalLabel = capitalize(plan.interval?.toString() ?? "");
    // Annual savings vs paying monthly for the same tier (2 months free →
    // ~$X/yr). Shown in green next to the interval when Annual is selected.
    const monthlyCounterpart = plans.find(
      (candidate) =>
        candidate.title === plan.title && matchesInterval(candidate, false),
    );
    const annualSavings =
      showAnnual && monthlyCounterpart
        ? monthlyCounterpart.amount * 12 - plan.amount
        : 0;
    /**
     * Annualised price, so two plans are comparable regardless of interval.
     *
     * `plan.amount` is Stripe's raw amount FOR ITS OWN INTERVAL, so comparing a
     * monthly plan's number against an annual one's is comparing $299 to $2,999 —
     * two different units. That is what the direction logic used to do, and on the
     * Annual view it mislabelled every column: against monthly Business, annual
     * Starter read "Downgrade" and annual Business / Individual / Enterprise all
     * read "Upgrade". Annual Business is $2,999/yr against monthly Business's
     * $3,588/yr — it is CHEAPER, and "Upgrade" was exactly backwards.
     */
    const annualised = (candidate: SubscriptionPlan) =>
      candidate.interval === "year" ? candidate.amount : candidate.amount * 12;

    // Same tier, different interval — a billing-cadence change, not a tier move.
    // Neither "Upgrade" nor "Downgrade" describes it, and the column is already
    // chipped "Current" because the matrix matches the current column by TITLE so
    // the highlight survives the interval toggle. Saying "Upgrade" directly beneath
    // a "Current" chip was the visible contradiction this resolves.
    const isSameTierOtherInterval =
      !!currentSubscribedPlan &&
      !isCurrentPlan &&
      plan.title.toLowerCase() === currentSubscribedPlan.title.toLowerCase();

    const isUpgrade =
      currentPlan && !isCurrentPlan
        ? annualised(plan) > annualised(currentPlan)
        : !isCurrentPlan;
    const renewsLabel =
      isCurrentPlan &&
      subscription?.status === "active" &&
      subscription?.current_period_end
        ? `Renews ${date(subscription.current_period_end)}`
        : null;
    const trialLabel =
      isCurrentPlan &&
      subscription?.status === "trialing" &&
      subscription?.trial_end
        ? `Trial ends ${date(subscription.trial_end)}`
        : null;
    // Net-new subscriber (no current plan) → "Subscribe"; otherwise frame
    // the move relative to the current plan's price.
    //
    // The current plan's button says "Current plan", NOT the renewal date. It used
    // to render `renewsLabel` ("Renews Aug 03 2026") because, when this was a row of
    // cards, the card was the only place that date could live. Two problems now that
    // the header sits in a ~197px column: the date wrapped to two lines inside a 40px
    // button, and the summary card directly above already states it — so the longest
    // string on the row was also the one duplicated word for word a few pixels up.
    // The date belongs in the summary; the button says what the column IS.
    const actionLabel = isCurrentPlan
      ? "Current plan"
      : isSameTierOtherInterval
      ? plan.interval === "year"
        ? "Switch to annual"
        : "Switch to monthly"
      : !currentPlan
      ? "Subscribe"
      : annualised(plan) > annualised(currentPlan)
      ? "Upgrade"
      : "Downgrade";
    const isActionDisabled = Boolean(
      renewsLabel || trialLabel || isCurrentPlan
    );
    return {
      isCurrentPlan,
      priceLabel,
      intervalLabel,
      annualSavings,
      isUpgrade,
      renewsLabel,
      trialLabel,
      actionLabel,
      isActionDisabled,
    };
  };

  const planAction = (plan: SubscriptionPlan): PlanColumnAction => {
    const info = getPlanColumnInfo(plan);
    return {
      label: info.actionLabel,
      variant: info.isUpgrade ? "primary" : "secondary",
      disabled: info.isActionDisabled,
      onClick: () => handlePlanChange(plan),
    };
  };

  const planSavingsLabel = (plan: SubscriptionPlan): string | null => {
    const { annualSavings } = getPlanColumnInfo(plan);
    return annualSavings > 0 ? `Save ${centsToDollar(annualSavings)}` : null;
  };

  /**
   * "On Business · $299 / Month · renews Aug 03 2026" — the line under the page
   * title. Null for a net-new subscriber, so the slot renders nothing rather than
   * a stub.
   *
   * Reads the CURRENT subscription, never the interval being browsed: toggling to
   * Annual must not change what this says you are on. That is precisely why the
   * line survives at all, since on Annual every column header shows a price the
   * company is not paying.
   *
   * Middots rather than em-dashes (§15) — three em-dashes in one line of UI copy
   * reads dense.
   */
  const currentPlanSummary = currentSubscribedPlan
    ? (() => {
        const info = getPlanColumnInfo(currentSubscribedPlan);
        const when = info.renewsLabel ?? info.trialLabel;
        // One string: the Header's `description` slot takes a line, and this is a
        // line. It was briefly split into {title, detail} so the matrix cell could
        // emphasise the tier name beside a chip; that cell no longer renders it.
        //
        // Lowercased mid-clause, and middots rather than em-dashes (§15) — three
        // em-dashes in one line of UI copy reads dense.
        return [
          `On ${currentSubscribedPlan.title}`,
          `${info.priceLabel} / ${info.intervalLabel}`,
          when
            ? when
                .replace(/^Renews /, "renews ")
                .replace(/^Trial ends /, "trial ends ")
            : null,
        ]
          .filter(Boolean)
          .join(" · ");
      })()
    : null;

  const canManageSubscription =
    !!subscription && subscription.status !== "canceled";

  const activeUntil = subscription
    ? subscription.status === "trialing"
      ? subscription.trial_end
        ? date(subscription.trial_end)
        : null
      : subscription.current_period_end
      ? date(subscription.current_period_end)
      : null
    : null;

  const handleCancelSubscription = async ({
    reason,
    feedback,
  }: {
    reason: string;
    feedback?: string;
  }) => {
    if (!canManageSubscription || cancelSubscription.isPending) return;
    try {
      await cancelSubscription.mutateAsync({ reason, feedback });
      notify({
        type: "success",
        title: "Subscription canceled",
        text: "Your subscription has been canceled successfully.",
      });
      setOpenCancelSubscriptionModal(false);
    } catch (error: any) {
      notify({
        type: "error",
        title: "Unable to cancel",
        text: error?.message ?? "Failed to cancel the subscription.",
      });
    }
  };

  const handleGrantFreeMonth = async () => {
    if (!canManageSubscription || grantFreeMonth.isPending) return;
    try {
      const { subscription: updated } = await grantFreeMonth.mutateAsync();
      const trialEndText = updated?.trial_end ? date(updated.trial_end) : null;
      notify({
        type: "success",
        title: "Free month applied",
        text: trialEndText
          ? `Enjoy your extra month. Your next renewal is now ${trialEndText}.`
          : "Enjoy your extra month.",
      });
      setOpenCancelSubscriptionModal(false);
    } catch (error: any) {
      notify({
        type: "error",
        title: "Unable to apply offer",
        text: error?.message ?? "Failed to apply the free month offer.",
      });
    }
  };

  const handleCloseCancelSubscriptionModal = () => {
    if (cancelSubscription.isPending || grantFreeMonth.isPending) return;
    setOpenCancelSubscriptionModal(false);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (isPlansError || filteredPlans.length === 0) {
    return (
      <EmptyState
        title="No plans available"
        text="We couldn't find any subscription plans. Please try again later."
        buttons={
          <Button onClick={() => navigate(ROUTES.SETTINGS_COMPANY_BILLING)}>
            Back to billing
          </Button>
        }
      />
    );
  }

  return (
    <>
      {/* No `rightComponents`. The Monthly/Annual control used to live here, top-right,
          which put it as far as the layout allows from the prices it changes — easy to
          miss, and it read as page chrome rather than as the switch driving every number
          below. It now sits in the matrix's own header cell, immediately left of those
          prices. Nothing replaced it: the page has one primary control and it belongs
          with the thing it controls. */}
      <Header
        title="Change Plan"
        breadcrumbsOveride={"Plan"}
        /**
         * Back under the title, where page context belongs (§40). It spent a stretch
         * inside the matrix's corner cell — put there to fill empty space — but a
         * comparison table's corner describing YOUR subscription is page-level
         * context in a component-level slot, and it read that way.
         *
         * It is not merely decorative: on the ANNUAL view every column shows annual
         * pricing (the current tier's header reads "$2,999 / Year"), so this is the
         * only place stating the actual $299 / Month, and the only place stating the
         * renewal date at all.
         */
        description={currentPlanSummary}
      />
      <PlanComparisonMatrix
        plans={filteredPlans}
        // Trialing counts as current here (see `currentSubscribedPlan`), so the
        // column highlight agrees with the summary card above it.
        currentPlanTitle={currentSubscribedPlan?.title ?? currentPlan?.title}
        planAction={planAction}
        planSavingsLabel={planSavingsLabel}
        intervalControl={
          /* The shared component, not the inline copy this page carried until
             2026-08-26 (the swap VRG-383 left for whoever next touched the file).
             The copy was visually identical, but it held its own hardcoded
             "Save 19%" — so the same wrong price claim had two homes and fixing
             one would have left the other. */
          <PlanIntervalToggle
            checked={showAnnual}
            onChange={setShowAnnual}
            savingsLabel={intervalSavingsLabel}
          />
        }
        /* A pointer down to the contact-sales card, as the last thing inside the
           matrix. That card sits immediately below — but "immediately below" only
           helps a reader who reaches the bottom of a table this tall, and the person
           who needs it is precisely the one reading the top tier's column and
           finding it short. Left-aligned in the label column, matching the collapse
           control above it. */
        footer={
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            onClick={handleContactSales}
            sx={{
              width: "fit-content",
              cursor: "pointer",
              mt: 3,
              color: theme.palette.text.secondary,
              "&:hover": { color: theme.palette.text.primary },
            }}
          >
            <Typography variant="body3" color="inherit">
              Need an SLA, data residency or procurement? Talk to us about
              Enterprise
            </Typography>
            <ArrowUpRightIcon size={14} strokeWidth={1.5} />
          </Stack>
        }
      />

      {/* The contact-sales tier, IMMEDIATELY below the grid (VRG-393). Adjacency is
          load-bearing: it is what anchors "Everything in <top plan>, plus" to the
          column it extends, and it is the mitigation for the one real cost of not
          being a fifth column — a card is easier to scroll past. Nothing goes
          between the two. `topPricedPlanTitle` is the last of the price-sorted
          plans, so the sentence follows a Stripe rename instead of hardcoding a
          tier name that just changed once already. */}
      <EnterpriseContactCard
        topPricedPlanTitle={filteredPlans[filteredPlans.length - 1]?.title}
        onContact={handleContactSales}
      />

      {/* The graduated activation schedule is normally IDENTICAL across every
          plan (all four currently share one metered price) — shown once here,
          below the matrix, rather than repeated per column where it would
          wrongly imply it varies by plan. Plans differ only in
          included_activations, which the matrix's own row already states.
          VRG-375: that sameness is no longer assumed —
          `scheduleAgreesAcrossPlans` (via `activationTiersAgree`) checks it
          against every plan's own `activation_tiers`, and the heading inside
          `ActivationPricingSchedule` drops the "same on every plan" claim if
          it's ever false. */}
      {/* No wrapping Stack/mt here — the table variant is itself a Card carrying the
          same `mt: 8` PlanComparisonMatrix uses, so wrapping it doubled the gap. */}
      <ActivationPricingSchedule
        variant="table"
        tiers={scheduleTiers}
        sameAcrossPlans={scheduleAgreesAcrossPlans}
      />

      {canManageSubscription && (
        <Stack
          mt={8}
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={3}
        >
          <Typography variant="body3" color="text.secondary">
            Want to unsubscribe? You can cancel your subscription at any time.
          </Typography>
          {/* A findable button (not buried inline text) but calm: secondary
              outline, neutral at rest, reddening only on hover (§32) so a rare,
              destructive action stays discoverable without inviting churn. */}
          <Button
            variant="secondary"
            destructive="hover"
            onClick={() => setOpenCancelSubscriptionModal(true)}
          >
            Cancel subscription
          </Button>
        </Stack>
      )}
      <CancelSubscriptionModal
        open={openCancelSubscriptionModal}
        onClose={handleCloseCancelSubscriptionModal}
        onConfirmCancel={handleCancelSubscription}
        onAcceptOffer={handleGrantFreeMonth}
        isCancelling={cancelSubscription.isPending}
        isAcceptingOffer={grantFreeMonth.isPending}
        activeUntil={activeUntil}
      />
    </>
  );
};
